import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #historyStorageKey = 'rizpoli_product_search_history';
  #historyLimit = 5;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    this.#recordCurrentProductPage();

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
      this.addEventListener('click', this.#handleHistoryClick, { signal });
    } else {
      this.addEventListener('click', this.#handleHistoryClick, { signal });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input.
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  /**
   * When the search dialog opens, show product history if the input is empty.
   */
  #handleDialogOpen = () => {
    if (this.refs.searchInput.value.trim() === '') {
      this.#renderProductHistory();
    }
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }

        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based.
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }

        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'nearest',
    });

    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');

        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }

        break;
      }
    }
  };

  /**
   * Handles product history delete / clear actions.
   * Product history is saved only after the product page is actually opened.
   * This avoids duplicated records and invalid links.
   *
   * @param {MouseEvent} event
   */
  #handleHistoryClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const deleteButton = target.closest('[data-riz-history-delete]');
    if (deleteButton instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();

      const key = deleteButton.getAttribute('data-riz-history-delete');

      if (key) {
        this.#removeProductHistoryRecord(key);
        this.#refreshProductHistoryDisplay();
      }

      return;
    }

    const clearButton = target.closest('[data-riz-history-clear]');
    if (clearButton instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();

      this.#clearProductHistory();
      this.#refreshProductHistoryDisplay();
    }
  };

  /**
   * Clears the recently viewed products.
   * This is kept for theme compatibility.
   *
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset.
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }

    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and show history if the search term is empty.
   */
  search = debounce((event) => {
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers.
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for.
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;
        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#appendProductHistoryToResults();
        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  #hideResetButton() {
    const { resetButton } = this.refs;
    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;
    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();

    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    this.#activeFetch = abortController;

    return abortController;
  }

  /**
   * Clean reset behavior:
   * - clear input
   * - hide reset button
   * - abort active search fetch
   * - render product text history
   */
  #resetSearch = async () => {
    const { searchInput } = this.refs;

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    this.#createAbortController();

    this.#renderProductHistory();
    this.#resetScrollPositions();
  };

  /**
   * Record the current product page as a text history item.
   * This is the only place where product history is saved.
   */
  #recordCurrentProductPage() {
    const productKey = this.#getProductKeyFromUrl(window.location.href);

    if (!productKey) return;

    const title = this.#getCurrentProductTitle();
    const url = this.#getCleanProductUrl(window.location.href);

    if (!title || !url) return;

    this.#saveProductHistoryRecord({
      title,
      url,
    });
  }

  #getCurrentProductTitle() {
    const selectors = [
      '[data-product-title]',
      '.product__title',
      '.product-title',
      'product-info h1',
      'main h1',
      'h1',
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);

      if (element instanceof HTMLElement) {
        const title = this.#cleanText(element.textContent || '');

        if (title) return title;
      }
    }

    return this.#cleanText(document.title.split('|')[0].split('–')[0].split('-')[0] || '');
  }

  #getProductHistory() {
    try {
      const rawHistory = window.localStorage.getItem(this.#historyStorageKey);

      if (!rawHistory) return [];

      const history = JSON.parse(rawHistory);

      if (!Array.isArray(history)) return [];

      const seenKeys = new Set();

      const cleanHistory = history
        .filter((item) => item && item.title && item.url)
        .map((item) => {
          const key = this.#getProductKeyFromUrl(item.url);
          const cleanUrl = this.#getCleanProductUrl(item.url);

          return {
            title: this.#cleanText(item.title),
            url: cleanUrl,
            key,
            timestamp: item.timestamp || Date.now(),
          };
        })
        .filter((item) => item.title && item.url && item.key)
        .filter((item) => {
          if (seenKeys.has(item.key)) return false;

          seenKeys.add(item.key);
          return true;
        })
        .slice(0, this.#historyLimit);

      window.localStorage.setItem(this.#historyStorageKey, JSON.stringify(cleanHistory));

      return cleanHistory;
    } catch (error) {
      return [];
    }
  }

  /**
   * @param {{ title: string, url: string }} record
   */
  #saveProductHistoryRecord(record) {
    const title = this.#cleanText(record.title);
    const url = this.#getCleanProductUrl(record.url);
    const key = this.#getProductKeyFromUrl(url);

    if (!title || !url || !key) return;

    const currentHistory = this.#getProductHistory();

    const nextHistory = [
      {
        title,
        url,
        key,
        timestamp: Date.now(),
      },
      ...currentHistory.filter((item) => item.key !== key),
    ].slice(0, this.#historyLimit);

    window.localStorage.setItem(this.#historyStorageKey, JSON.stringify(nextHistory));
  }

  #removeProductHistoryRecord(key) {
    const nextHistory = this.#getProductHistory().filter((item) => item.key !== key);

    window.localStorage.setItem(this.#historyStorageKey, JSON.stringify(nextHistory));
  }

  #clearProductHistory() {
    window.localStorage.removeItem(this.#historyStorageKey);
  }

  #refreshProductHistoryDisplay() {
    if (this.refs.searchInput.value.trim()) {
      this.#appendProductHistoryToResults();
    } else {
      this.#renderProductHistory();
    }
  }

  #renderProductHistory() {
    const { predictiveSearchResults } = this.refs;
    const history = this.#getProductHistory();

    if (!history.length) {
      predictiveSearchResults.innerHTML = '';
      predictiveSearchResults.setAttribute('aria-expanded', 'false');
      return;
    }

    predictiveSearchResults.innerHTML = this.#getProductHistoryMarkup(false);
    predictiveSearchResults.setAttribute('aria-expanded', 'true');
  }

  #appendProductHistoryToResults() {
    const { predictiveSearchResults } = this.refs;
    const history = this.#getProductHistory();

    const oldHistory = predictiveSearchResults.querySelector('.riz-search-history');
    if (oldHistory) {
      oldHistory.remove();
    }

    if (!history.length) return;

    const inner =
      predictiveSearchResults.querySelector('#predictive-search-results .predictive-search-results__inner') ||
      predictiveSearchResults.querySelector('.predictive-search-results__inner');

    if (!inner) return;

    inner.insertAdjacentHTML('beforeend', this.#getProductHistoryMarkup(true));
  }

  #getProductHistoryMarkup(isUnderResults = false) {
    const history = this.#getProductHistory();

    if (!history.length) return '';

    const historyItemsMarkup = history
      .map((item) => {
        const title = this.#escapeHtml(item.title);
        const url = this.#escapeAttribute(item.url);
        const key = this.#escapeAttribute(item.key);

        return `
          <li class="riz-search-history__item">
            <a class="riz-search-history__link" href="${url}">
              ${title}
            </a>

            <button
              type="button"
              class="riz-search-history__delete button button-unstyled"
              data-riz-history-delete="${key}"
              aria-label="Supprimer ${title}"
            >
              ×
            </button>
          </li>
        `;
      })
      .join('');

    if (isUnderResults) {
      return `
        <div class="riz-search-history riz-search-history--under-results">
          <div class="riz-search-history__header">
            <h4 class="predictive-search-results__title riz-search-history__title">
              Historique
            </h4>

            <button
              type="button"
              class="riz-search-history__clear button button-unstyled"
              data-riz-history-clear
            >
              Tout effacer
            </button>
          </div>

          <ul class="riz-search-history__list list-unstyled">
            ${historyItemsMarkup}
          </ul>
        </div>
      `;
    }

    return `
      <div
        id="predictive-search-results"
        class="predictive-search-dropdown riz-search-history-dropdown"
        role="listbox"
        aria-expanded="true"
        data-transition-container
      >
        <div class="predictive-search-results__inner riz-search-history">
          <div class="riz-search-history__header">
            <h4 class="predictive-search-results__title riz-search-history__title">
              Historique
            </h4>

            <button
              type="button"
              class="riz-search-history__clear button button-unstyled"
              data-riz-history-clear
            >
              Tout effacer
            </button>
          </div>

          <ul class="riz-search-history__list list-unstyled">
            ${historyItemsMarkup}
          </ul>
        </div>
      </div>
    `;
  }

  #getProductKeyFromUrl(url) {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      const match = parsedUrl.pathname.match(/\/products\/([^/?#]+)/);

      if (!match || !match[1]) return '';

      return decodeURIComponent(match[1]).replace(/\/$/, '');
    } catch (error) {
      return '';
    }
  }

  #getCleanProductUrl(url) {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      const match = parsedUrl.pathname.match(/\/products\/([^/?#]+)/);

      if (!match || !match[1]) return '';

      parsedUrl.search = '';
      parsedUrl.hash = '';

      return parsedUrl.origin + parsedUrl.pathname.replace(/\/$/, '');
    } catch (error) {
      return '';
    }
  }

  #cleanText(text) {
    return String(text || '').replace(/\s+/g, ' ').trim();
  }

  #escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  #escapeAttribute(value) {
    return this.#escapeHtml(value);
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}